import { detectShots } from './detectShots';
import { encodeFrame } from './encodeFrame';
import { sampleFrames } from './sampleFrames';
import type { VideoDecoder } from './videoDecoder';

import type { ImageContent, TextContent } from 'js/src/agent/types';

/**
 * "Tile strip" encoding of a video for a VLM.
 *
 * A video is segmented into shots (perceptual-hash cut detection), one
 * representative frame is chosen per shot, and frames are composited into
 * horizontal strips where each cell is exactly one 512px OpenAI tile. Each
 * strip image is paired with a text block that labels every panel with its
 * shot time range and the narration spoken during that shot, so the model
 * correlates visuals with the transcript in place.
 *
 * Sizing each cell to a single tile avoids the ~33% token overhead of sending
 * N separate images (85 + 170·N tokens per N-cell strip vs 255·N for N images).
 */

/** Each shot occupies one 512px-wide OpenAI tile in the composited strip. */
export const TILE_WIDTH = 512;
export const JPEG_QUALITY = 0.75;

/**
 * Shot detection segments a video by editorial *cuts*. That works well on
 * edited videos (Lumen5 renders, montages) but collapses raw single-take
 * footage — a drone flyover, a product spin, a continuous pan — into one shot,
 * so the model sees a single representative frame for the whole clip.
 *
 * The frame budget is a *floor, not a cap*: if fewer than FRAME_BUDGET shots
 * were detected we subdivide the longest shots in time until we reach the
 * budget, giving under-cut footage real coverage. Videos that already have at
 * least FRAME_BUDGET shots exceed the floor and are left byte-for-byte
 * unchanged — the token-efficient x4 strips for cut-heavy videos don't move.
 */
export const FRAME_BUDGET = 12;

/**
 * Never subdivide a shot into slices shorter than this. Bounds redundancy on
 * short clips (splitting a 4s clip into 12 frames would just yield near
 * duplicates) — such clips stop short of the full budget rather than over-sample.
 */
export const MIN_SEGMENT_S = 2;

export interface TileTranscriptWord {
    word: string;
    startTime: number;
    endTime: number;
}

export interface ShotWindow {
    startS: number;
    endS: number;
}

export interface StripPanel {
    /** 0-based index of the shot across the whole video. */
    shotIndex: number;
    window: ShotWindow;
    transcript: string;
    /** Set when the shot was subdivided by the frame budget; identifies this slice within its shot. */
    segment?: { index: number; count: number } | null;
}

export interface VideoTileStripMeta {
    durationS: number;
    widthPx: number;
    heightPx: number;
    languageCode?: string | null;
}

/**
 * Turn cut timestamps into contiguous [startS, endS) shot windows spanning the
 * whole clip. Always returns at least one window (the full duration).
 */
export function buildShotWindows(cutTimestampsS: number[], durationS: number): ShotWindow[] {
    const cuts = cutTimestampsS.filter((t) => t > 0 && t < durationS).sort((a, b) => a - b);
    const boundaries = [0, ...cuts, durationS];
    const windows: ShotWindow[] = [];
    for (let i = 0; i < boundaries.length - 1; i++) {
        const startS = boundaries[i];
        const endS = boundaries[i + 1];
        if (endS > startS) windows.push({ startS, endS });
    }
    return windows.length > 0 ? windows : [{ startS: 0, endS: Math.max(0, durationS) }];
}

/**
 * A shot window after the frame budget has been applied. When a shot was
 * subdivided, `segment` carries the 0-based slice index and total slice count
 * so the panel can be labelled as part of its parent shot; it is `null` for a
 * whole (un-subdivided) shot.
 */
export interface BudgetedWindow {
    window: ShotWindow;
    /** 0-based index of the original detected shot this window came from. */
    shotIndex: number;
    segment: { index: number; count: number } | null;
}

/**
 * Decide how many panels each detected shot gets so the total reaches `budget`,
 * without ever dropping a shot. Every shot starts with one panel; the remaining
 * budget is handed out greedily to whichever shot currently has the longest
 * per-panel time slice, so the coarsest coverage is always subdivided first. A
 * shot is never split finer than `minSegmentS`, so short clips stop early
 * rather than producing near-duplicate frames. When the detected shot count
 * already meets or exceeds the budget, every shot gets exactly one panel and
 * the output is identical to the un-budgeted pipeline.
 */
export function allocatePanelCounts(
    windows: ShotWindow[],
    budget: number,
    minSegmentS: number,
): number[] {
    const counts = windows.map(() => 1);
    const caps = windows.map((w) => Math.max(1, Math.floor((w.endS - w.startS) / minSegmentS)));
    let remaining = budget - windows.length;
    while (remaining > 0) {
        let bestIdx = -1;
        let longestSlice = -Infinity;
        for (let i = 0; i < windows.length; i++) {
            if (counts[i] >= caps[i]) continue;
            const slice = (windows[i].endS - windows[i].startS) / counts[i];
            if (slice > longestSlice) {
                longestSlice = slice;
                bestIdx = i;
            }
        }
        if (bestIdx === -1) break;
        counts[bestIdx]++;
        remaining--;
    }
    return counts;
}

/** Split a window into `count` contiguous equal-duration slices (count <= 1 returns it unchanged). */
export function subdivideWindow(window: ShotWindow, count: number): ShotWindow[] {
    if (count <= 1) return [window];
    const step = (window.endS - window.startS) / count;
    const slices: ShotWindow[] = [];
    for (let i = 0; i < count; i++) {
        slices.push({
            startS: window.startS + i * step,
            endS: i === count - 1 ? window.endS : window.startS + (i + 1) * step,
        });
    }
    return slices;
}

/**
 * Expand detected shot windows up to `budget` panels by subdividing the longest
 * shots in time. A no-op (one BudgetedWindow per shot, no segments) when the
 * detected shot count already meets the budget.
 */
export function applyFrameBudget(
    windows: ShotWindow[],
    budget: number = FRAME_BUDGET,
    minSegmentS: number = MIN_SEGMENT_S,
): BudgetedWindow[] {
    const counts = allocatePanelCounts(windows, budget, minSegmentS);
    const budgeted: BudgetedWindow[] = [];
    windows.forEach((window, shotIndex) => {
        const count = counts[shotIndex];
        subdivideWindow(window, count).forEach((slice, index) => {
            budgeted.push({
                window: slice,
                shotIndex,
                segment: count > 1 ? { index, count } : null,
            });
        });
    });
    return budgeted;
}

/**
 * Assign each transcript word to the shot whose window contains its start time,
 * returning the joined narration per shot (empty string for silent shots).
 */
export function bucketWordsByShot(words: TileTranscriptWord[], windows: ShotWindow[]): string[] {
    const buckets: string[][] = windows.map(() => []);
    if (windows.length === 0) return [];
    for (const word of words) {
        let idx = windows.findIndex((w) => word.startTime >= w.startS && word.startTime < w.endS);
        // Words at or past the final boundary fall into the last shot.
        if (idx < 0 && word.startTime >= windows[windows.length - 1].endS) {
            idx = windows.length - 1;
        }
        if (idx >= 0) buckets[idx].push(word.word);
    }
    return buckets.map((b) => b.join(' ').trim());
}

/** Chunk shot indices into strips of at most `perStrip` shots. */
export function groupShotsIntoStrips(shotCount: number, perStrip: number): number[][] {
    const groups: number[][] = [];
    for (let i = 0; i < shotCount; i += perStrip) {
        const group: number[] = [];
        for (let j = i; j < Math.min(i + perStrip, shotCount); j++) group.push(j);
        groups.push(group);
    }
    return groups;
}

/** Pixel height of one 512px-wide cell for a frame of the given dimensions. */
export function cellHeightForFrame(
    widthPx: number,
    heightPx: number,
    tileWidth: number = TILE_WIDTH,
): number {
    if (widthPx <= 0 || heightPx <= 0) return tileWidth;
    return Math.round(tileWidth * (heightPx / widthPx));
}

/**
 * How many cells fit in one strip. Landscape/square cells are ~1 tile tall so 4
 * fit within OpenAI's 2048px long-side envelope; taller cells count as ~2 tiles,
 * so cap at 2 to stay within the short-side limit.
 */
export function perStripForCellHeight(
    cellHeightPx: number,
    tileWidth: number = TILE_WIDTH,
): number {
    return cellHeightPx <= tileWidth ? 4 : 2;
}

export function buildStripText(
    stripIndex: number,
    totalStrips: number,
    panels: StripPanel[],
): string {
    const lines = [
        `=== Strip ${stripIndex + 1} of ${totalStrips} (panels left to right below) ===`,
    ];
    panels.forEach((panel, i) => {
        const range = `${panel.window.startS.toFixed(1)}s–${panel.window.endS.toFixed(1)}s`;
        const narration = panel.transcript ? `: ${panel.transcript}` : ' (no narration)';
        const shotLabel = panel.segment
            ? `Shot ${panel.shotIndex + 1} (part ${panel.segment.index + 1}/${panel.segment.count})`
            : `Shot ${panel.shotIndex + 1}`;
        lines.push(`Panel ${i + 1} = ${shotLabel} (${range})${narration}`);
    });
    return lines.join('\n');
}

function loadImage(dataUrl: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error('Failed to load frame image for tile strip'));
        img.src = dataUrl;
    });
}

/** Composite frame data-URLs left-to-right into a single JPEG strip. */
export async function compositeStrip(
    dataUrls: string[],
    cellWidthPx: number,
    cellHeightPx: number,
): Promise<string> {
    const canvas = document.createElement('canvas');
    canvas.width = cellWidthPx * dataUrls.length;
    canvas.height = cellHeightPx;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Could not acquire 2D context for tile strip');
    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    const images = await Promise.all(dataUrls.map(loadImage));
    images.forEach((img, i) => {
        ctx.drawImage(img, i * cellWidthPx, 0, cellWidthPx, cellHeightPx);
    });
    return canvas.toDataURL('image/jpeg', JPEG_QUALITY);
}

export interface EncodeVideoAsTileStripArgs {
    decoder: VideoDecoder;
    meta: VideoTileStripMeta;
    transcriptWords: TileTranscriptWord[];
}

/**
 * Full pipeline: sample frames → detect shots → apply the frame budget
 * (subdivide long shots when the clip is under-cut) → pick one representative
 * frame per panel → bucket transcript by panel → composite tile strips
 * interleaved with per-panel text. Returns multimodal content ready for
 * `EnhancedToolResult`.
 */
export async function encodeVideoAsTileStrip({
    decoder,
    meta,
    transcriptWords,
}: EncodeVideoAsTileStripArgs): Promise<(TextContent | ImageContent)[]> {
    const samples = await sampleFrames(decoder, meta.durationS);
    if (samples.length === 0) {
        return [
            {
                type: 'text',
                text: 'Video analysis — no frames could be extracted (the clip reported no usable duration).',
            },
        ];
    }
    const { cutTimestampsS } = detectShots(samples);
    const shotWindows = buildShotWindows(cutTimestampsS, meta.durationS);
    const budgeted = applyFrameBudget(shotWindows);
    const windows = budgeted.map((b) => b.window);
    const transcripts = bucketWordsByShot(transcriptWords, windows);

    const cellHeightPx = cellHeightForFrame(meta.widthPx, meta.heightPx);
    const perStrip = perStripForCellHeight(cellHeightPx);

    // Reuse the already-decoded sample nearest each shot's midpoint rather than
    // seeking again — the sampling grid is dense enough to be representative.
    const frameDataUrls = windows.map((w) => {
        const mid = (w.startS + w.endS) / 2;
        let best = samples[0];
        let bestDist = Infinity;
        for (const s of samples) {
            const dist = Math.abs(s.timeS - mid);
            if (dist < bestDist) {
                bestDist = dist;
                best = s;
            }
        }
        return encodeFrame(best.canvas, TILE_WIDTH);
    });

    const groups = groupShotsIntoStrips(windows.length, perStrip);
    const parts: (TextContent | ImageContent)[] = [];

    const shotCount = shotWindows.length;
    const panelCount = windows.length;
    const avgPanelS = panelCount ? meta.durationS / panelCount : meta.durationS;
    const hasNarration = transcripts.some((t) => t.length > 0);
    const languageSuffix = hasNarration && meta.languageCode ? ` (${meta.languageCode})` : '';
    const expandedNote =
        panelCount > shotCount ? ` (expanded to ${panelCount} panels for coverage)` : '';
    parts.push({
        type: 'text',
        text:
            `Video analysis — ${meta.durationS.toFixed(1)}s, ${shotCount} shot(s) detected${expandedNote}, ` +
            `~${avgPanelS.toFixed(1)}s per panel. ` +
            `Transcript: ${hasNarration ? `present${languageSuffix}` : 'none'}. ` +
            `Frames below are grouped into tile strips; each panel is a representative ` +
            `frame, labelled with its time range and any narration.`,
    });

    for (let g = 0; g < groups.length; g++) {
        const group = groups[g];
        const panels: StripPanel[] = group.map((panelIndex) => ({
            shotIndex: budgeted[panelIndex].shotIndex,
            window: budgeted[panelIndex].window,
            transcript: transcripts[panelIndex] ?? '',
            segment: budgeted[panelIndex].segment,
        }));
        parts.push({ type: 'text', text: buildStripText(g, groups.length, panels) });
        const stripImage = await compositeStrip(
            group.map((i) => frameDataUrls[i]),
            TILE_WIDTH,
            cellHeightPx,
        );
        parts.push({ type: 'image_url', image_url: { url: stripImage } });
    }

    return parts;
}
